import React, { useState, useEffect, useMemo, useCallback } from "react";
import { displayDomainLabel } from "@/reports/telecom/displayDomainLabel";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Map, ExternalLink, Layers, LayoutList, CalendarRange, Clock } from "lucide-react";
import { RoadmapTimeline } from "./RoadmapTimeline";
import { groupByHorizon, HORIZON_ORDER, HORIZON_META } from "@/reports/telecom/roadmapHorizons";
import { resolveFindingControlLabel } from "@/lib/findingControlLabel";
import { resolveFindingFrameworkPills } from "@/lib/findingFrameworkPills";
import { FindingFrameworkPills } from "@/components/reviewer/FindingFrameworkPills";

const sb: any = supabase as any;

interface RoadmapRow {
  id: string;
  display_id?: string | null;
  display_seq?: number | null;
  assessment_id: string;
  question_id: number;
  control_code: string;
  finding_title: string;
  risk_level: string;
  priority_level: string;
  timeframe: string;
  recommended_action: string;
  recommended_measures: string;
  remediation_path: string;
  csv_domain: string | null;
  nist_function: string | null;
  source_type: string | null;
  source_key: string | null;
  control_label: string;
  created_at: string;
  owners: string[];
  due_date: string | null;
  cap_status: string | null;
}


interface RemediationRoadmapTabProps {
  assessmentId: string;
  disabled?: boolean;
  questionnaireKey?: string | null;
  onNavigateToFinding?: (questionId: number) => void;
  /** Bumped by parent on tab switch to force a refetch (cross-tab sync). */
  reloadKey?: number;
}

const RISK_COLORS: Record<string, string> = {
  Critical: "bg-red-700 text-white",
  HIGH: "bg-destructive text-destructive-foreground",
  High: "bg-destructive text-destructive-foreground",
  MODERATE: "bg-yellow-500/90 text-white",
  Moderate: "bg-yellow-500/90 text-white",
  LOW: "bg-green-600 text-white",
  Low: "bg-green-600 text-white",
};
const PRIORITY_COLORS: Record<string, string> = {
  Immediate: "bg-destructive/10 text-destructive border-destructive/30",
  "Quick Win": "bg-yellow-500/10 text-yellow-700 border-yellow-500/30",
  Strategic: "bg-blue-500/10 text-blue-700 border-blue-500/30",
};

const DEFAULT_TIMEFRAMES: Record<string, string> = {
  Critical: "30 days",
  HIGH: "90 days",
  High: "90 days",
  MODERATE: "180 days",
  Moderate: "180 days",
  LOW: "365 days",
  Low: "365 days",
};

export const RemediationRoadmapTab: React.FC<RemediationRoadmapTabProps> = ({
  assessmentId,
  questionnaireKey,
  onNavigateToFinding,
  reloadKey,
}) => {
  const isTelecomV2 = questionnaireKey === "telecom_v2";
  const [rows, setRows] = useState<RoadmapRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupByDomain, setGroupByDomain] = useState(false);
  const [groupByHorizons, setGroupByHorizons] = useState<boolean>(isTelecomV2);
  const [viewMode, setViewMode] = useState<"table" | "timeline">("table");

  const loadRoadmap = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const { data, error } = await sb.rpc("reviewer_get_roadmap", {
        p_assessment_id: assessmentId,
      });
      if (error) {
        console.warn("RemediationRoadmap load error:", error);
        setRows([]);
        return;
      }
      const mapped: RoadmapRow[] = (data || []).map((f: any) => ({
        ...f,
        assessment_id: assessmentId,
        control_code: f.control_code ?? "",
        finding_title: f.finding_title ?? "",
        risk_level: f.risk_level ?? "",
        priority_level: f.priority_level ?? "",
        timeframe: f.timeframe || DEFAULT_TIMEFRAMES[f.risk_level] || "",
        recommended_action: f.recommended_action ?? "",
        recommended_measures: f.recommended_measures ?? "",
        remediation_path: f.remediation_path ?? "",
        csv_domain: f.csv_domain ?? null,
        nist_function: f.nist_function ?? null,
        source_type: f.source_type ?? null,
        source_key: f.source_key ?? f.infrastructure_control_key ?? null,
        control_label: resolveFindingControlLabel({
          source_type: f.source_type,
          source_key: f.source_key ?? f.infrastructure_control_key,
          control_code: f.control_code,
        }),
        owners: Array.isArray(f.owners) ? f.owners : [],
        due_date: f.due_date ?? null,
        cap_status: f.cap_status ?? null,
      }));
      setRows(mapped);
    } catch (e) {
      console.warn("RemediationRoadmap error:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadRoadmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRoadmap, reloadKey]);


  const metrics = useMemo(() => {
    const total = rows.length;
    const critical = rows.filter((r) => r.risk_level === "Critical").length;
    const high = rows.filter((r) => r.risk_level === "HIGH" || r.risk_level === "High").length;
    const moderate = rows.filter((r) => r.risk_level === "MODERATE" || r.risk_level === "Moderate").length;
    const low = rows.filter((r) => r.risk_level === "LOW" || r.risk_level === "Low").length;
    return { total, critical, high, moderate, low };
  }, [rows]);

  const groupedByDomain = useMemo(() => {
    if (!groupByDomain) return null;
    const groups: Record<string, RoadmapRow[]> = {};
    for (const row of rows) {
      const domain = row.csv_domain || "Uncategorized";
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(row);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [rows, groupByDomain]);

  const horizonGroups = useMemo(() => {
    if (!isTelecomV2 || !groupByHorizons) return null;
    return groupByHorizon(rows);
  }, [rows, groupByHorizons, isTelecomV2]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        Loading remediation roadmap…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        <Map className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="font-medium">No remediation items yet.</p>
        <p className="text-sm mt-1">Findings created in the Assessment Results or Infrastructure Security tabs will automatically populate this roadmap.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricCard label="Total Remediations" value={metrics.total} className="bg-card" />
        <MetricCard label="Critical" value={metrics.critical} className="bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400" />
        <MetricCard label="High" value={metrics.high} className="bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400" />
        <MetricCard label="Moderate" value={metrics.moderate} className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400" />
        <MetricCard label="Low" value={metrics.low} className="bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400" />
      </div>

      <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        This is a read-only execution view. All remediation activity — owners, due
        dates, status, conversation, and evidence — is managed in the{" "}
        <span className="font-medium text-foreground">Corrective Action Plan</span> tab.
      </div>

      <div className="flex items-center justify-between bg-card rounded-lg border p-3">
        <span className="text-sm text-muted-foreground">{rows.length} remediation items</span>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              className={`flex items-center gap-1 text-xs px-3 py-1.5 transition-colors ${
                viewMode === "table"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
              onClick={() => setViewMode("table")}
            >
              <LayoutList className="h-3.5 w-3.5" />
              Table
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 text-xs px-3 py-1.5 transition-colors ${
                viewMode === "timeline"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
              onClick={() => setViewMode("timeline")}
            >
              <CalendarRange className="h-3.5 w-3.5" />
              Timeline
            </button>
          </div>

          {viewMode === "table" && isTelecomV2 && (
            <button
              type="button"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
                groupByHorizons
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:bg-accent"
              }`}
              onClick={() => {
                setGroupByHorizons((v) => !v);
                if (!groupByHorizons) setGroupByDomain(false);
              }}
            >
              <Clock className="h-3.5 w-3.5" />
              Phased Horizons
            </button>
          )}

          {viewMode === "table" && (
            <button
              type="button"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
                groupByDomain
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:bg-accent"
              }`}
              onClick={() => {
                setGroupByDomain(!groupByDomain);
                if (!groupByDomain) setGroupByHorizons(false);
              }}
            >
              <Layers className="h-3.5 w-3.5" />
              Group by Domain
            </button>
          )}
        </div>
      </div>

      {viewMode === "timeline" ? (
        <RoadmapTimeline items={rows} />
      ) : horizonGroups ? (
        HORIZON_ORDER.map((horizon) => {
          const horizonRows = horizonGroups[horizon];
          if (!horizonRows.length) return null;
          const meta = HORIZON_META[horizon];
          return (
            <div key={horizon} className="space-y-2">
              <div className="flex items-start justify-between gap-3 px-1">
                <div>
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    {meta.label}
                    <span className="text-xs font-normal text-muted-foreground">({meta.window})</span>
                    <Badge variant="secondary" className="text-[10px]">{horizonRows.length}</Badge>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">{meta.description}</p>
                </div>
              </div>
              <RoadmapTable
                rows={horizonRows}
                onNavigateToFinding={onNavigateToFinding}
              />
            </div>
          );
        })
      ) : groupByDomain && groupedByDomain ? (
        groupedByDomain.map(([domain, domainRows]) => (
          <div key={domain} className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 px-1">
              <Layers className="h-4 w-4 text-primary" />
              {domain}
              <Badge variant="secondary" className="text-[10px]">{domainRows.length}</Badge>
            </h3>
            <RoadmapTable
              rows={domainRows}
              onNavigateToFinding={onNavigateToFinding}
            />
          </div>
        ))
      ) : (
        <RoadmapTable
          rows={rows}
          onNavigateToFinding={onNavigateToFinding}
        />
      )}
    </div>
  );
};

// --- Read-only Roadmap Table ---
const RoadmapTable: React.FC<{
  rows: RoadmapRow[];
  onNavigateToFinding?: (questionId: number) => void;
}> = ({ rows, onNavigateToFinding }) => (
  <div className="bg-card rounded-lg border">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[140px]">Control</TableHead>
          <TableHead className="w-[140px]">Domain</TableHead>
          <TableHead>Finding</TableHead>
          <TableHead className="w-[90px]">Risk</TableHead>
          <TableHead className="w-[90px]">Priority</TableHead>
          <TableHead className="w-[110px]">Timeframe</TableHead>
          <TableHead className="w-[180px]">Owner(s)</TableHead>
          <TableHead className="w-[110px]">Due Date</TableHead>
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className={r.source_type === "infrastructure_security" ? "text-xs" : "font-mono text-xs"}>
              {r.control_label || r.control_code || "—"}
            </TableCell>
            <TableCell className="text-xs">{displayDomainLabel(r.csv_domain, r.source_type)}</TableCell>
            <TableCell className="text-sm">
              <div className="flex flex-col gap-1">
                <div className="flex items-start gap-2">
                  {r.display_id && (
                    <span
                      className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
                      title="Finding tracking reference (persistent within this assessment)"
                    >
                      {r.display_id}
                    </span>
                  )}
                  <span>{r.finding_title || <span className="italic text-muted-foreground">No title</span>}</span>
                </div>
                <FindingFrameworkPills
                  pills={resolveFindingFrameworkPills({
                    source_type: r.source_type,
                    source_key: (r as any).source_key ?? (r as any).infrastructure_control_key ?? null,
                    nist_function: r.nist_function,
                  })}
                />
              </div>
            </TableCell>
            <TableCell>
              <Badge className={`${RISK_COLORS[r.risk_level] ?? "bg-muted text-muted-foreground"} text-[10px] px-1.5 py-0`}>
                {r.risk_level || "—"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${PRIORITY_COLORS[r.priority_level] ?? ""}`}>
                {r.priority_level || "—"}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">{r.timeframe || "—"}</TableCell>
            <TableCell className="text-xs">
              {r.owners.length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {r.owners.map((o) => (
                    <Badge key={o} variant="secondary" className="text-[10px]">{o}</Badge>
                  ))}
                </div>
              )}
            </TableCell>
            <TableCell className="text-xs">
              {r.due_date ? new Date(r.due_date).toLocaleDateString() : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell>
              {onNavigateToFinding && (
                <button
                  type="button"
                  title="Edit in Findings tab"
                  className="p-1 text-primary hover:text-primary/80"
                  onClick={() => onNavigateToFinding(r.question_id)}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);


const MetricCard: React.FC<{ label: string; value: number; className?: string }> = ({ label, value, className }) => (
  <div className={`rounded-lg border p-3 text-center ${className ?? ""}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs font-medium mt-0.5">{label}</div>
  </div>
);

export default RemediationRoadmapTab;
